// Consent: Actions for getting and updating consent answers.
import { Dispatch } from 'redux';

import { get, put, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppState } from '../reducers';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;

const CUSTOMER_CONSENT_RESOURCE_PATH = '/dagligbank-customer-consent-web/rest/resource';
// Must match the `ConsentDomain` enum in the consent service (case-sensitive). PM = personmarked.
const CONSENT_DOMAIN = 'PM';

// type FetchConsentRequest = import('../generated-types').NyttSamtykkesvar;
export type UpdateConsent = {
    name: string;
    value: 'JA' | 'NEI' | 'UBESVART' | string;
};
// type FetchConsentResponse = import('../generated-types').Samtykke; // TODO: Add response in back-end
type FetchConsentResponse = {
    samtykkeId: {
        value: string;
    };
    tittel: string;
    // Texts are resolved locally (indexed on `samtykkeId.value`), the back-end
    // currently returns an empty array here.
    tekster: string[];
};

export interface FetchConsentStartedAction extends Action<typeof actionTypes.FETCH_CONSENT_STARTED> {
    type: typeof actionTypes.FETCH_CONSENT_STARTED;
}

export interface FetchConsentFulfilledAction extends Action<typeof actionTypes.FETCH_CONSENT_FULFILLED> {
    type: typeof actionTypes.FETCH_CONSENT_FULFILLED;
    payload: FetchConsentResponse[];
}

export interface FetchConsentFailedAction extends Action<typeof actionTypes.FETCH_CONSENT_FAILED> {
    type: typeof actionTypes.FETCH_CONSENT_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface UpdateConsentAction extends Action<typeof actionTypes.UPDATE_CONSENT> {
    type: typeof actionTypes.UPDATE_CONSENT;
    payload: UpdateConsent;
}

export interface SaveConsentStartedAction extends Action<typeof actionTypes.SAVE_CONSENT_STARTED> {
    type: typeof actionTypes.SAVE_CONSENT_STARTED;
}

export interface SaveConsentFulfilledAction extends Action<typeof actionTypes.SAVE_CONSENT_FULFILLED> {
    type: typeof actionTypes.SAVE_CONSENT_FULFILLED;
    payload: unknown;
}

export interface SaveConsentFailedAction extends Action<typeof actionTypes.SAVE_CONSENT_FAILED> {
    type: typeof actionTypes.SAVE_CONSENT_FAILED;
    payload: RequestExceptionWithResponse;
}

export type ConsentActions =
    | FetchConsentStartedAction
    | FetchConsentFulfilledAction
    | FetchConsentFailedAction
    | UpdateConsentAction
    | SaveConsentStartedAction
    | SaveConsentFulfilledAction
    | SaveConsentFailedAction;

export const fetchConsentStarted = (): FetchConsentStartedAction => ({
    type: actionTypes.FETCH_CONSENT_STARTED,
});
export const fetchConsentFulfilled = (json: FetchConsentResponse[]): FetchConsentFulfilledAction => ({
    type: actionTypes.FETCH_CONSENT_FULFILLED,
    payload: json,
});
export const fetchConsentFailed = (error: RequestExceptionWithResponse): FetchConsentFailedAction => ({
    type: actionTypes.FETCH_CONSENT_FAILED,
    payload: error,
});

export const updateConsent = (name: UpdateConsent['name'], value: UpdateConsent['value']): UpdateConsentAction => ({
    type: actionTypes.UPDATE_CONSENT,
    payload: { name, value },
});

export const saveConsentStarted = (): SaveConsentStartedAction => ({
    type: actionTypes.SAVE_CONSENT_STARTED,
});
export const saveConsentFulfilled = (): SaveConsentFulfilledAction => ({
    type: actionTypes.SAVE_CONSENT_FULFILLED,
    payload: {},
});
export const saveConsentFailed = (error: RequestExceptionWithResponse): SaveConsentFailedAction => ({
    type: actionTypes.SAVE_CONSENT_FAILED,
    payload: error,
});

export const fetchConsent = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { bankregnr } = getState().context.config;
    try {
        dispatch(fetchConsentStarted());
        const response: FetchConsentResponse[] = await get(
            `${actionTypes.RESOURCE_PATH}/consent`,
            getRequestHeaders(bankregnr),
        );
        dispatch(fetchConsentFulfilled(response));
    } catch (error) {
        dispatch(fetchConsentFailed(error));
    }
};

export const saveConsent = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { consent, context, feature } = getState();
    // When the consent page is skipped via toggle, there is nothing to collect/save.
    if (feature.salgspantSkipConsent) return;
    if (consent?.data?.length) {
        const updates = consent.data
            .map((c: any) => ({
                type: c.samtykkeId.value,
                choice: (c.svar || 'UBESVART') === 'JA',
                svar: c.svar || 'UBESVART',
            }))
            .filter((c: any) => c.svar !== 'UBESVART')
            .map((c: any) => ({
                type: c.type,
                choice: c.choice,
            }));

        try {
            dispatch(saveConsentStarted());
            await Promise.all(
                updates.map((update: any) =>
                    put(
                        `${CUSTOMER_CONSENT_RESOURCE_PATH}/${CONSENT_DOMAIN}/consent/update`,
                        update,
                        getRequestHeaders(context.config.bankregnr),
                    ),
                ),
            );
            dispatch(saveConsentFulfilled());
        } catch (error) {
            dispatch(saveConsentFailed(error));
        }
    }
};
